import posthog from 'posthog-js';
import { APP_CONFIG } from 'src/config/app.config';
import { ANALYTICS_CONFIG } from 'src/config/analytics.config';
import { Company, User } from 'src/constants/types';
import { isBhiAdmin } from 'src/helpers/permissions';

// Resolves straight from APP_CONFIG's own hostname keys rather than
// axiosClient's IS_DEV_ENV/IS_QA_ENV — those collapse `local` into `dev`
// once they've picked an API_BASE_URL (api-dev.agencytext.com contains
// "dev"), which is exactly what let local sessions write into the dev
// PostHog project.
const envKey = (Object.keys(APP_CONFIG).find(key =>
  window.location.hostname.includes(key),
) as keyof typeof ANALYTICS_CONFIG) || 'dev';
const envConfig = ANALYTICS_CONFIG[envKey];

let isInitialized = false;

export function initAnalytics(): void {
  if (!envConfig.enabled || !envConfig.apiKey || isInitialized) return;

  posthog.init(envConfig.apiKey, {
    api_host: envConfig.apiHost,
    person_profiles: 'identified_only',
    // Contact/message screens surface names and phone numbers throughout the
    // DOM — leave autocapture off until each screen has been reviewed for
    // what it would scrape, and rely on explicit pageview + manual events.
    autocapture: false,
    // Almost all navigation here is React Router pushState, not full page
    // loads — `true` only fires one pageview at init, `'history_change'` is
    // what actually hooks the History API to capture route changes too.
    capture_pageview: 'history_change',
    // Same PII surface as autocapture, but reachable from the PostHog
    // project UI alone: recording starts as soon as the project-side toggle
    // is on unless the client explicitly opts out here. Hard-disable so a
    // dashboard change can't silently start replaying contact/message
    // screens.
    disable_session_recording: true,
    // Independent of `autocapture` — defaults to whatever the PostHog
    // project's "Enable heatmaps" setting is (on by default for new
    // projects), and runs a background mouse/scroll/click tracker that
    // flushes on its own timer regardless of navigation. Hard-disable for
    // the same reason as session recording.
    capture_heatmaps: false,
  });
  isInitialized = true;
}

export function identifyAnalyticsUser(user: User, company: Company): void {
  if (!isInitialized) return;

  posthog.identify(user.id, {
    role: user.role?.name,
    is_internal: isBhiAdmin(user),
  });

  setAnalyticsCompany(company);
}

// `posthog.group()` is sticky for the session — call this wherever the
// active company can change without a full page reload (identifyAnalyticsUser
// already covers it on login/reload), or events keep tagging the old company.
export function setAnalyticsCompany(company: Company): void {
  if (!isInitialized) return;

  posthog.group('company', company.id, {
    name: company.name,
    applicationId: company.applicationId,
    status: company.status,
  });
}

export function resetAnalyticsUser(): void {
  if (!isInitialized) return;
  posthog.reset();
}
